import { useTheme, type SxProps, type Theme } from '@mui/material/styles';
import { ReactNode } from 'react';
import { StatCardColor, getStatCardTokens } from '../theme/statCardTheme';

import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
export interface StatCardProps {
  color: StatCardColor;
  label: string;
  value: ReactNode;
  icon?: ReactNode;
  onClick?: () => void;
  active?: boolean;
  disabled?: boolean;
  sx?: SxProps<Theme>;
}

export function StatCard({
  color,
  label,
  value,
  icon,
  onClick,
  active,
  disabled,
  sx,
}: StatCardProps) {
  const theme = useTheme();
  const tokens = getStatCardTokens(theme, color);

  return (
    <Card
      component={ButtonBase}
      onClick={onClick}
      disabled={disabled}
      sx={{
        minWidth: 250,
        ...sx,
        bgcolor: active ? tokens.hoverBackground : tokens.background,
        borderColor: tokens.border,
        borderWidth: 1,
        borderStyle: 'solid',
        boxShadow: active ? tokens.hoverShadow : 'none',
        transform: active ? 'translateY(-1px)' : undefined,
        borderRadius: 2,
        cursor: onClick ? 'pointer' : 'default',
        textAlign: 'left',
        display: 'block',
        transition: theme.transitions.create(
          ['transform', 'box-shadow', 'background-color'],
          {
            duration: theme.transitions.duration.short,
          }
        ),
        ':hover': {
          boxShadow: tokens.hoverShadow,
          bgcolor: tokens.hoverBackground,
          transform: 'translateY(-1px)',
        },
        ...sx,
      }}
    >
      <CardContent
        sx={{
          py: 1,
          px: 1.5,
          '&:last-child': {
            pb: 1,
          },
        }}
      >
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack spacing={0.25}>
            <Typography variant="body2" sx={{ color: tokens.textColor }}>
              {label}
            </Typography>
            <Typography
              variant="h5"
              sx={{
                color: tokens.textColor,
                fontWeight: 400,
                lineHeight: '133%',
              }}
            >
              {value}
            </Typography>
          </Stack>
          {icon && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 40,
                height: 40,
                borderRadius: 2.5,
                bgcolor: tokens.iconBackground,
                color: tokens.iconColor,
              }}
            >
              {icon}
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
